"use client";

import { useCallback, useSyncExternalStore } from "react";

import {
  CUSTOMER_OWNED_SESSION_STORAGE_BARRIER_KEY,
  isCustomerOwnedSessionStorageQuarantined,
} from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import type {
  CustomerFavouriteLocationCatalog,
  CustomerFavouritesOwner,
} from "@/app/favourites/domain/customerFavouritesContracts";
import { validateCustomerFavouriteLocationCatalog } from "@/app/favourites/domain/customerFavouritesValidation";

export const CUSTOMER_FAVOURITES_PREVIEW_STORAGE_SCHEMA_VERSION = 2 as const;
export const CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KEY_PREFIX =
  "volera:customer-favourites-preview:v2:";
const LEGACY_CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KEY_PREFIX =
  "volera:customer-favourites-preview:v1:";
export const CUSTOMER_FAVOURITES_PREVIEW_CHANGE_EVENT =
  "volera:customer-favourites-preview-change";
const CUSTOMER_FAVOURITES_PREVIEW_WEB_LOCK_PREFIX =
  "volera:customer-favourites-preview-envelope:v1:";
const CUSTOMER_FAVOURITES_PREVIEW_GLOBAL_WRITE_FENCE_KEY =
  "volera:customer-favourites-preview-write-fence:v1:global";
const CUSTOMER_FAVOURITES_PREVIEW_WRITE_LEASE_KEY_PREFIX =
  "volera:customer-favourites-preview-write-lease:v2:";

const CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KIND =
  "customer-favourites-preview";
const MAX_IDENTIFIER_LENGTH = 160;
const MAX_LOCATION_IDS = 1_000;
const IDENTIFIER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,159}$/u;
const EMPTY_LOCATION_IDS = Object.freeze([]) as readonly string[];

export interface CustomerFavouritesStoreContext {
  owner: CustomerFavouritesOwner;
  catalog: CustomerFavouriteLocationCatalog;
}

export type CustomerFavouritesLocalPersistence =
  | "browser-local"
  | "memory-fallback"
  | "none";

export interface CustomerFavouritesPreviewSnapshot {
  status: "ready";
  evidence: "frontend-fixture-preview";
  backendSynchronization: "not-claimed";
  owner: Readonly<CustomerFavouritesOwner>;
  locationIds: readonly string[];
  persistence: CustomerFavouritesLocalPersistence;
}

export type CustomerFavouritesPreviewState =
  | CustomerFavouritesPreviewSnapshot
  | {
      status: "unavailable";
      reason: "invalid-owner" | "invalid-catalog" | "runtime-unavailable";
    };

export type CustomerFavouritePreviewOperation =
  | "initialize"
  | "add"
  | "remove"
  | "toggle";

export type CustomerFavouritePreviewMutationResult =
  | {
      status: "preview-applied" | "preview-unchanged";
      operation: CustomerFavouritePreviewOperation;
      effect: "added" | "removed" | null;
      evidence: "frontend-fixture-preview";
      backendSynchronization: "not-claimed";
      persistence: CustomerFavouritesLocalPersistence;
      locationIds: readonly string[];
    }
  | {
      status: "preview-rejected";
      operation: CustomerFavouritePreviewOperation;
      reason:
        | "invalid-owner"
        | "invalid-catalog"
        | "invalid-location-id"
        | "location-unavailable"
        | "atomic-mutation-unavailable"
        | "runtime-unavailable";
      evidence: "frontend-fixture-preview";
      backendSynchronization: "not-claimed";
    };

interface StoredCustomerFavouritesPreviewV2 {
  schemaVersion: typeof CUSTOMER_FAVOURITES_PREVIEW_STORAGE_SCHEMA_VERSION;
  kind: typeof CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KIND;
  customerAccountId: string;
  providerId: string;
  providerRevision: string;
  catalogRevision: string;
  writeFenceToken: string;
  locationIds: readonly string[];
}

interface ResolvedCustomerFavouritesStoreContext {
  owner: Readonly<CustomerFavouritesOwner>;
  ownerKey: string;
  storageKey: string;
  catalogRevision: string;
  catalogFingerprint: string;
  publicLocationIds: ReadonlySet<string>;
}

interface MemoryRecord {
  exists: boolean;
  persistence: CustomerFavouritesLocalPersistence;
  record: StoredCustomerFavouritesPreviewV2;
}

interface CustomerFavouritesPreviewWriteFence {
  readonly globalToken: string;
  readonly customerToken: string;
  readonly customerLeaseKey: string;
}

interface CustomerFavouritesPreviewTeardownFence {
  readonly token: string;
}

interface CapturedCustomerFavouritesPreviewWriteFence {
  readonly status: "ready";
  readonly storage: Storage;
  readonly globalToken: string;
}

type CustomerFavouritesPreviewWriteFenceCapture =
  | CapturedCustomerFavouritesPreviewWriteFence
  | { readonly status: "teardown-active" }
  | { readonly status: "unavailable" };

type CustomerFavouritesPreviewWriteFenceState =
  | { readonly status: "idle"; readonly token: string }
  | { readonly status: "active"; readonly token: string };

interface CustomerFavouritesPreviewCrossContextLock {
  readonly name: string;
}

interface CustomerFavouritesPreviewCrossContextLockManager {
  request<T>(
    name: string,
    options: {
      readonly mode: "exclusive";
      readonly signal?: AbortSignal;
    },
    callback: (
      lock: CustomerFavouritesPreviewCrossContextLock | null,
    ) => T | PromiseLike<T>,
  ): Promise<T>;
}

type ContextResolution =
  | { valid: true; context: ResolvedCustomerFavouritesStoreContext }
  | { valid: false; reason: "invalid-owner" | "invalid-catalog" };

type EffectiveRecord = MemoryRecord & {
  context: ResolvedCustomerFavouritesStoreContext;
  repair: "none" | "normalize-current" | "replace-with-seed";
};

const memoryRecords = new Map<string, MemoryRecord>();
const unpersistedStorageKeys = new Set<string>();
const pendingClearStorageKeys = new Set<string>();
const snapshotCache = new Map<
  string,
  { fingerprint: string; snapshot: CustomerFavouritesPreviewSnapshot }
>();
const recoverablePreviewTeardownTokens = new Set<string>();
const runningPreviewTeardownTokens = new Set<string>();

let observedStorage: Storage | null = null;
let previewTeardownGeneration = 0;

const RUNTIME_UNAVAILABLE_STATE = Object.freeze({
  status: "unavailable",
  reason: "runtime-unavailable",
} satisfies CustomerFavouritesPreviewState);
const INVALID_OWNER_STATE = Object.freeze({
  status: "unavailable",
  reason: "invalid-owner",
} satisfies CustomerFavouritesPreviewState);
const INVALID_CATALOG_STATE = Object.freeze({
  status: "unavailable",
  reason: "invalid-catalog",
} satisfies CustomerFavouritesPreviewState);

function isRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  expectedKeys: readonly string[],
) {
  const keys = Object.keys(value);

  return (
    keys.length === expectedKeys.length &&
    keys.every((key) => expectedKeys.includes(key))
  );
}

function isIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= MAX_IDENTIFIER_LENGTH &&
    IDENTIFIER_PATTERN.test(value)
  );
}

function isOwner(value: unknown): value is CustomerFavouritesOwner {
  if (!isRecord(value)) {
    return false;
  }

  return (
    hasOnlyKeys(value, [
      "customerAccountId",
      "providerId",
      "providerRevision",
      "snapshotId",
      "snapshotRevision",
      "displayRevision",
    ]) &&
    isIdentifier(value.customerAccountId) &&
    isIdentifier(value.providerId) &&
    isIdentifier(value.providerRevision) &&
    isIdentifier(value.snapshotId) &&
    isIdentifier(value.snapshotRevision) &&
    isIdentifier(value.displayRevision)
  );
}

function resolveCatalogLocationIds(value: unknown):
  | {
      valid: true;
      catalogRevision: string;
      catalogFingerprint: string;
      publicLocationIds: ReadonlySet<string>;
    }
  | { valid: false } {
  try {
    const catalog = validateCustomerFavouriteLocationCatalog(value);

    if (!catalog) {
      return { valid: false };
    }

    const publicLocationIds = new Set<string>();
    const catalogLocationIds: string[] = [];

    for (const location of catalog.locations) {
      catalogLocationIds.push(
        `${location.locationId}:${String(location.publicationState)}`,
      );

      if (location.publicationState === "public") {
        publicLocationIds.add(location.locationId);
      }
    }

    return {
      valid: true,
      catalogRevision: catalog.displayRevision,
      catalogFingerprint: JSON.stringify(catalogLocationIds),
      publicLocationIds,
    };
  } catch {
    return { valid: false };
  }
}

function cloneOwner(owner: CustomerFavouritesOwner) {
  return Object.freeze({
    customerAccountId: owner.customerAccountId,
    providerId: owner.providerId,
    providerRevision: owner.providerRevision,
    snapshotId: owner.snapshotId,
    snapshotRevision: owner.snapshotRevision,
    displayRevision: owner.displayRevision,
  });
}

export function buildCustomerFavouritesOwnerKey(
  owner: CustomerFavouritesOwner,
) {
  if (!isOwner(owner)) {
    return null;
  }

  return JSON.stringify([
    owner.customerAccountId,
    owner.providerId,
    owner.providerRevision,
    owner.snapshotId,
    owner.snapshotRevision,
    owner.displayRevision,
  ]);
}

export function buildCustomerFavouritesPreviewStorageKey(
  customerAccountId: string,
) {
  return isIdentifier(customerAccountId)
    ? `${CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KEY_PREFIX}${encodeURIComponent(customerAccountId)}`
    : null;
}

function buildLegacyCustomerFavouritesPreviewStorageKey(
  customerAccountId: string,
) {
  return isIdentifier(customerAccountId)
    ? `${LEGACY_CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KEY_PREFIX}${encodeURIComponent(customerAccountId)}`
    : null;
}

function resolveContext(
  context: CustomerFavouritesStoreContext,
): ContextResolution {
  const ownerKey = buildCustomerFavouritesOwnerKey(context?.owner);

  if (!ownerKey) {
    return { valid: false, reason: "invalid-owner" };
  }

  const catalog = resolveCatalogLocationIds(context.catalog);

  if (
    !catalog.valid ||
    catalog.catalogRevision !== context.owner.displayRevision
  ) {
    return { valid: false, reason: "invalid-catalog" };
  }

  const storageKey = buildCustomerFavouritesPreviewStorageKey(
    context.owner.customerAccountId,
  );

  if (!storageKey) {
    return { valid: false, reason: "invalid-owner" };
  }

  return {
    valid: true,
    context: {
      owner: cloneOwner(context.owner),
      ownerKey,
      storageKey,
      catalogRevision: catalog.catalogRevision,
      catalogFingerprint: catalog.catalogFingerprint,
      publicLocationIds: catalog.publicLocationIds,
    },
  };
}

function resetMemoryForReplacementStorage() {
  memoryRecords.clear();
  unpersistedStorageKeys.clear();
  pendingClearStorageKeys.clear();
  snapshotCache.clear();
  recoverablePreviewTeardownTokens.clear();
  runningPreviewTeardownTokens.clear();
}

function getBrowserStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storage = window.localStorage;

    if (
      !storage ||
      typeof storage.getItem !== "function" ||
      typeof storage.setItem !== "function" ||
      typeof storage.removeItem !== "function"
    ) {
      return null;
    }

    if (observedStorage && observedStorage !== storage) {
      resetMemoryForReplacementStorage();
    }

    observedStorage = storage;
    return storage;
  } catch {
    return null;
  }
}

function getCrossContextLockManager(): CustomerFavouritesPreviewCrossContextLockManager | null {
  if (typeof navigator === "undefined") {
    return null;
  }

  const lockManager = (
    navigator as Navigator & {
      locks?: CustomerFavouritesPreviewCrossContextLockManager;
    }
  ).locks;

  return lockManager && typeof lockManager.request === "function"
    ? lockManager
    : null;
}

function buildPreviewEnvelopeWebLockName(customerAccountId: string) {
  return `${CUSTOMER_FAVOURITES_PREVIEW_WEB_LOCK_PREFIX}${encodeURIComponent(customerAccountId)}`;
}

function buildPreviewWriteLeaseKey(customerAccountId: string) {
  return `${CUSTOMER_FAVOURITES_PREVIEW_WRITE_LEASE_KEY_PREFIX}${encodeURIComponent(customerAccountId)}`;
}

function isOpaquePreviewToken(value: unknown): value is string {
  return typeof value === "string" && /^[a-f0-9]{32}$/u.test(value);
}

function parsePreviewWriteFenceState(
  value: string | null,
): CustomerFavouritesPreviewWriteFenceState | null {
  if (value === null) {
    return Object.freeze({ status: "idle", token: "initial" });
  }

  const match = /^(active|idle):([a-f0-9]{32})$/u.exec(value);

  if (!match) {
    return null;
  }

  return Object.freeze({
    status: match[1] as "active" | "idle",
    token: match[2],
  });
}

function readPreviewWriteFenceState(
  storage: Storage,
): CustomerFavouritesPreviewWriteFenceState | null {
  try {
    return parsePreviewWriteFenceState(
      storage.getItem(CUSTOMER_FAVOURITES_PREVIEW_GLOBAL_WRITE_FENCE_KEY),
    );
  } catch {
    return null;
  }
}

function capturePreviewWriteFence(): CustomerFavouritesPreviewWriteFenceCapture {
  const storage = getBrowserStorage();

  if (!storage) {
    return Object.freeze({ status: "unavailable" });
  }

  const fenceState = readPreviewWriteFenceState(storage);

  if (!fenceState) {
    return Object.freeze({ status: "unavailable" });
  }

  if (fenceState.status === "active") {
    return Object.freeze({ status: "teardown-active" });
  }

  return Object.freeze({
    status: "ready",
    storage,
    globalToken: fenceState.token,
  });
}

function acquirePreviewWriteFence(
  storage: Storage,
  customerAccountId: string,
  expectedGlobalToken: string,
):
  | {
      readonly status: "ready";
      readonly fence: CustomerFavouritesPreviewWriteFence;
      readonly createdLease: boolean;
    }
  | { readonly status: "fence-changed" | "storage-unavailable" } {
  const initialFenceState = readPreviewWriteFenceState(storage);

  if (
    initialFenceState?.status !== "idle" ||
    initialFenceState.token !== expectedGlobalToken
  ) {
    return Object.freeze({ status: "fence-changed" });
  }

  const customerLeaseKey = buildPreviewWriteLeaseKey(customerAccountId);
  let customerToken: string;
  let createdLease = false;

  try {
    const storedToken = storage.getItem(customerLeaseKey);

    if (storedToken === null) {
      const newToken = createPreviewWriteFenceToken();

      if (!newToken) {
        return Object.freeze({ status: "storage-unavailable" });
      }

      storage.setItem(customerLeaseKey, newToken);
      if (storage.getItem(customerLeaseKey) !== newToken) {
        return Object.freeze({ status: "storage-unavailable" });
      }

      customerToken = newToken;
      createdLease = true;
    } else if (isOpaquePreviewToken(storedToken)) {
      customerToken = storedToken;
    } else {
      return Object.freeze({ status: "storage-unavailable" });
    }
  } catch {
    return Object.freeze({ status: "storage-unavailable" });
  }

  const fence = Object.freeze({
    globalToken: expectedGlobalToken,
    customerToken,
    customerLeaseKey,
  });

  if (!previewWriteFenceIsCurrent(storage, fence)) {
    if (createdLease) {
      releaseUnusedPreviewWriteLease(storage, fence);
    }
    return Object.freeze({ status: "fence-changed" });
  }

  return Object.freeze({ status: "ready", fence, createdLease });
}

function releaseUnusedPreviewWriteLease(
  storage: Storage,
  fence: CustomerFavouritesPreviewWriteFence,
) {
  try {
    if (storage.getItem(fence.customerLeaseKey) === fence.customerToken) {
      storage.removeItem(fence.customerLeaseKey);
    }

    return storage.getItem(fence.customerLeaseKey) !== fence.customerToken;
  } catch {
    return false;
  }
}

function previewWriteFencesMatch(
  left: CustomerFavouritesPreviewWriteFence,
  right: CustomerFavouritesPreviewWriteFence,
) {
  return (
    left.globalToken === right.globalToken &&
    left.customerToken === right.customerToken &&
    left.customerLeaseKey === right.customerLeaseKey
  );
}

function previewWriteFenceIsCurrent(
  storage: Storage,
  expectedFence: CustomerFavouritesPreviewWriteFence,
) {
  const currentState = readPreviewWriteFenceState(storage);

  if (
    currentState?.status !== "idle" ||
    currentState.token !== expectedFence.globalToken
  ) {
    return false;
  }

  try {
    const currentCustomerToken = storage.getItem(
      expectedFence.customerLeaseKey,
    );
    const currentFence = Object.freeze({
      globalToken: currentState.token,
      customerToken: currentCustomerToken ?? "",
      customerLeaseKey: expectedFence.customerLeaseKey,
    });
    return previewWriteFencesMatch(currentFence, expectedFence);
  } catch {
    return false;
  }
}

function createPreviewWriteFenceToken() {
  if (typeof crypto === "undefined") {
    return null;
  }

  try {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
      "",
    );
  } catch {
    return null;
  }
}

function beginPreviewTeardown(
  storage: Storage,
): CustomerFavouritesPreviewTeardownFence | null {
  const currentState = readPreviewWriteFenceState(storage);

  if (!currentState) {
    return null;
  }

  if (currentState.status === "active") {
    if (
      !recoverablePreviewTeardownTokens.has(currentState.token) ||
      runningPreviewTeardownTokens.has(currentState.token)
    ) {
      return null;
    }

    runningPreviewTeardownTokens.add(currentState.token);
    return Object.freeze({ token: currentState.token });
  }

  const token = createPreviewWriteFenceToken();

  if (!token) {
    return null;
  }

  const serialized = `active:${token}`;

  try {
    storage.setItem(
      CUSTOMER_FAVOURITES_PREVIEW_GLOBAL_WRITE_FENCE_KEY,
      serialized,
    );
    const acquired = storage.getItem(
      CUSTOMER_FAVOURITES_PREVIEW_GLOBAL_WRITE_FENCE_KEY,
    ) === serialized;

    if (!acquired) {
      return null;
    }

    recoverablePreviewTeardownTokens.add(token);
    runningPreviewTeardownTokens.add(token);
    return Object.freeze({ token });
  } catch {
    return null;
  }
}

function previewTeardownIsCurrent(
  storage: Storage,
  teardownFence: CustomerFavouritesPreviewTeardownFence,
) {
  const currentState = readPreviewWriteFenceState(storage);
  return (
    currentState?.status === "active" &&
    currentState.token === teardownFence.token
  );
}

function finishPreviewTeardownAttempt(
  teardownFence: CustomerFavouritesPreviewTeardownFence,
  completed: boolean,
) {
  runningPreviewTeardownTokens.delete(teardownFence.token);
  if (completed) {
    recoverablePreviewTeardownTokens.delete(teardownFence.token);
  }
}

function completePreviewTeardown(
  storage: Storage,
  teardownFence: CustomerFavouritesPreviewTeardownFence,
) {
  const activeValue = `active:${teardownFence.token}`;
  const idleToken = createPreviewWriteFenceToken();

  if (!idleToken) {
    return false;
  }

  const idleValue = `idle:${idleToken}`;

  try {
    if (
      storage.getItem(CUSTOMER_FAVOURITES_PREVIEW_GLOBAL_WRITE_FENCE_KEY) !==
      activeValue
    ) {
      return false;
    }

    storage.setItem(
      CUSTOMER_FAVOURITES_PREVIEW_GLOBAL_WRITE_FENCE_KEY,
      idleValue,
    );
    return (
      storage.getItem(CUSTOMER_FAVOURITES_PREVIEW_GLOBAL_WRITE_FENCE_KEY) ===
      idleValue
    );
  } catch {
    return false;
  }
}

function createStoredRecord(
  context: ResolvedCustomerFavouritesStoreContext,
  locationIds: readonly string[],
  writeFenceToken: string,
): StoredCustomerFavouritesPreviewV2 {
  return Object.freeze({
    schemaVersion: CUSTOMER_FAVOURITES_PREVIEW_STORAGE_SCHEMA_VERSION,
    kind: CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KIND,
    customerAccountId: context.owner.customerAccountId,
    providerId: context.owner.providerId,
    providerRevision: context.owner.providerRevision,
    catalogRevision: context.catalogRevision,
    writeFenceToken,
    locationIds: Object.freeze([...locationIds]),
  });
}

function recordMatchesContext(
  record: StoredCustomerFavouritesPreviewV2,
  context: ResolvedCustomerFavouritesStoreContext,
) {
  return (
    record.customerAccountId === context.owner.customerAccountId &&
    record.providerId === context.owner.providerId &&
    record.providerRevision === context.owner.providerRevision &&
    record.catalogRevision === context.catalogRevision
  );
}

function parseStoredRecord(
  value: unknown,
  context: ResolvedCustomerFavouritesStoreContext,
  expectedWriteFenceToken: string,
): StoredCustomerFavouritesPreviewV2 | null {
  try {
    if (
      !isRecord(value) ||
      !hasOnlyKeys(value, [
        "schemaVersion",
        "kind",
        "customerAccountId",
        "providerId",
        "providerRevision",
        "catalogRevision",
        "writeFenceToken",
        "locationIds",
      ]) ||
      value.schemaVersion !==
        CUSTOMER_FAVOURITES_PREVIEW_STORAGE_SCHEMA_VERSION ||
      value.kind !== CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KIND ||
      !isIdentifier(value.customerAccountId) ||
      !isIdentifier(value.providerId) ||
      !isIdentifier(value.providerRevision) ||
      !isIdentifier(value.catalogRevision) ||
      value.writeFenceToken !== expectedWriteFenceToken ||
      !Array.isArray(value.locationIds) ||
      value.locationIds.length > MAX_LOCATION_IDS ||
      !value.locationIds.every(isIdentifier)
    ) {
      return null;
    }

    const record = createStoredRecord(
      context,
      value.locationIds,
      expectedWriteFenceToken,
    );

    if (
      record.customerAccountId !== value.customerAccountId ||
      record.providerId !== value.providerId ||
      record.providerRevision !== value.providerRevision ||
      record.catalogRevision !== value.catalogRevision ||
      record.writeFenceToken !== value.writeFenceToken ||
      !recordMatchesContext(record, context)
    ) {
      return null;
    }

    return record;
  } catch {
    return null;
  }
}

function normalizeLocationIds(
  locationIds: readonly string[],
  context: ResolvedCustomerFavouritesStoreContext,
) {
  const seenLocationIds = new Set<string>();
  const normalized: string[] = [];

  for (const locationId of locationIds) {
    if (
      seenLocationIds.has(locationId) ||
      !context.publicLocationIds.has(locationId)
    ) {
      continue;
    }

    seenLocationIds.add(locationId);
    normalized.push(locationId);
  }

  return normalized;
}

function dispatchPreviewChange(storageKey: string) {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(CUSTOMER_FAVOURITES_PREVIEW_CHANGE_EVENT, {
      detail: Object.freeze({ storageKey }),
    }),
  );
}

function publishMemoryFallbackRecord(
  context: ResolvedCustomerFavouritesStoreContext,
  record: StoredCustomerFavouritesPreviewV2,
): "memory-fallback" {
  const memoryRecord: MemoryRecord = {
    exists: true,
    persistence: "memory-fallback",
    record,
  };

  memoryRecords.set(context.storageKey, memoryRecord);
  unpersistedStorageKeys.add(context.storageKey);
  dispatchPreviewChange(context.storageKey);
  return "memory-fallback";
}

function getMemoryFallback(
  context: ResolvedCustomerFavouritesStoreContext,
): MemoryRecord {
  const current = memoryRecords.get(context.storageKey);

  if (current && recordMatchesContext(current.record, context)) {
    return {
      ...current,
      persistence: "memory-fallback",
    };
  }

  const fallback: MemoryRecord = {
    exists: false,
    persistence: "memory-fallback",
    record: createStoredRecord(context, EMPTY_LOCATION_IDS, "memory"),
  };
  return fallback;
}

function readEffectiveRecord(
  context: ResolvedCustomerFavouritesStoreContext,
): EffectiveRecord {
  // Observe a replaced storage object before consulting per-storage fallback
  // markers. Test runtimes, privacy-mode recovery, and embedded hosts can swap
  // the Storage implementation; stale memory-only flags must never bind the
  // replacement to the previous runtime's state.
  const storage = getBrowserStorage();

  if (pendingClearStorageKeys.has(context.storageKey)) {
    const emptyRecord = createStoredRecord(
      context,
      EMPTY_LOCATION_IDS,
      "memory",
    );
    const clearedFallback = {
      exists: false,
      persistence: "memory-fallback" as const,
      record: emptyRecord,
    };
    return {
      ...clearedFallback,
      context,
      repair: "replace-with-seed",
    };
  }

  if (unpersistedStorageKeys.has(context.storageKey)) {
    return {
      ...getMemoryFallback(context),
      context,
      repair: "none",
    };
  }

  if (!storage) {
    return {
      ...getMemoryFallback(context),
      context,
      repair: "none",
    };
  }

  let writeFenceToken: string | null;

  try {
    const storedWriteFenceToken = storage.getItem(
      buildPreviewWriteLeaseKey(context.owner.customerAccountId),
    );
    writeFenceToken = isOpaquePreviewToken(storedWriteFenceToken)
      ? storedWriteFenceToken
      : null;
  } catch {
    return {
      ...getMemoryFallback(context),
      context,
      repair: "none",
    };
  }

  let raw: string | null;

  try {
    raw = storage.getItem(context.storageKey);
  } catch {
    return {
      ...getMemoryFallback(context),
      context,
      repair: "none",
    };
  }

  if (raw === null) {
    const missing: MemoryRecord = {
      exists: false,
      persistence: "none",
      record: createStoredRecord(
        context,
        EMPTY_LOCATION_IDS,
        writeFenceToken ?? "missing",
      ),
    };
    return {
      ...missing,
      context,
      repair: "replace-with-seed",
    };
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }

  const record = writeFenceToken
    ? parseStoredRecord(parsed, context, writeFenceToken)
    : null;

  if (!record) {
    const emptyRecord = createStoredRecord(
      context,
      EMPTY_LOCATION_IDS,
      writeFenceToken ?? "missing",
    );

    return {
      context,
      exists: false,
      persistence: "none",
      record: emptyRecord,
      repair: "replace-with-seed",
    };
  }

  const normalizedLocationIds = normalizeLocationIds(
    record.locationIds,
    context,
  );

  if (
    JSON.stringify(normalizedLocationIds) !==
    JSON.stringify(record.locationIds)
  ) {
    const normalizedRecord = createStoredRecord(
      context,
      normalizedLocationIds,
      record.writeFenceToken,
    );

    return {
      context,
      exists: true,
      persistence: "browser-local",
      record: normalizedRecord,
      repair: "normalize-current",
    };
  }

  return {
    context,
    exists: true,
    persistence: "browser-local",
    record,
    repair: "none",
  };
}

function unavailableState(
  reason: Extract<CustomerFavouritesPreviewState, { status: "unavailable" }>[
    "reason"
  ],
): CustomerFavouritesPreviewState {
  switch (reason) {
    case "invalid-owner":
      return INVALID_OWNER_STATE;
    case "invalid-catalog":
      return INVALID_CATALOG_STATE;
    case "runtime-unavailable":
      return RUNTIME_UNAVAILABLE_STATE;
  }
}

function readResolvedPreview(
  context: ResolvedCustomerFavouritesStoreContext,
): CustomerFavouritesPreviewSnapshot {
  const effective = readEffectiveRecord(context);
  const locationIds = effective.record.locationIds;
  const fingerprint = JSON.stringify([
    context.ownerKey,
    context.catalogFingerprint,
    effective.persistence,
    locationIds,
  ]);
  const cached = snapshotCache.get(context.ownerKey);

  if (cached?.fingerprint === fingerprint) {
    return cached.snapshot;
  }

  const snapshot = Object.freeze({
    status: "ready",
    evidence: "frontend-fixture-preview",
    backendSynchronization: "not-claimed",
    owner: context.owner,
    locationIds,
    persistence: effective.persistence,
  } satisfies CustomerFavouritesPreviewSnapshot);

  snapshotCache.set(context.ownerKey, { fingerprint, snapshot });
  return snapshot;
}

export function readCustomerFavouritesPreview(
  context: CustomerFavouritesStoreContext,
): CustomerFavouritesPreviewState {
  const resolution = resolveContext(context);

  if (!resolution.valid) {
    return unavailableState(resolution.reason);
  }

  if (typeof window === "undefined") {
    return unavailableState("runtime-unavailable");
  }

  if (isCustomerOwnedSessionStorageQuarantined()) {
    return unavailableState("runtime-unavailable");
  }

  const storage = getBrowserStorage();
  const writeFenceState = storage
    ? readPreviewWriteFenceState(storage)
    : null;

  if (storage && (!writeFenceState || writeFenceState.status === "active")) {
    return unavailableState("runtime-unavailable");
  }

  if (storage) {
    try {
      const customerLease = storage.getItem(
        buildPreviewWriteLeaseKey(
          resolution.context.owner.customerAccountId,
        ),
      );

      if (customerLease !== null && !isOpaquePreviewToken(customerLease)) {
        return unavailableState("runtime-unavailable");
      }
    } catch {
      return unavailableState("runtime-unavailable");
    }
  }

  const snapshot = readResolvedPreview(resolution.context);

  if (storage) {
    const currentStorage = getBrowserStorage();
    const currentFenceState = currentStorage
      ? readPreviewWriteFenceState(currentStorage)
      : null;

    if (
      isCustomerOwnedSessionStorageQuarantined() ||
      currentStorage !== storage ||
      currentFenceState?.status !== "idle" ||
      currentFenceState.token !== writeFenceState?.token
    ) {
      return unavailableState("runtime-unavailable");
    }
  }

  return snapshot;
}

function isMatchingStorageEvent(
  event: StorageEvent,
  relevantStorageKeys: ReadonlySet<string>,
) {
  if (event.key !== null && !relevantStorageKeys.has(event.key)) {
    return false;
  }

  if (event.storageArea === null) {
    return true;
  }

  try {
    return event.storageArea === window.localStorage;
  } catch {
    return false;
  }
}

export function subscribeToCustomerFavouritesPreview(
  customerAccountId: string,
  onStoreChange: () => void,
) {
  const storageKey = buildCustomerFavouritesPreviewStorageKey(
    customerAccountId,
  );

  if (typeof window === "undefined" || !storageKey) {
    return () => {};
  }

  const relevantStorageKeys = new Set([
    storageKey,
    buildPreviewWriteLeaseKey(customerAccountId),
    CUSTOMER_FAVOURITES_PREVIEW_GLOBAL_WRITE_FENCE_KEY,
    CUSTOMER_OWNED_SESSION_STORAGE_BARRIER_KEY,
  ]);

  const handleSameTabChange = (event: Event) => {
    const detail = (event as CustomEvent<{ storageKey?: unknown }>).detail;

    if (detail?.storageKey === storageKey) {
      onStoreChange();
    }
  };
  const handleStorageChange = (event: StorageEvent) => {
    if (isMatchingStorageEvent(event, relevantStorageKeys)) {
      // A different browsing context owns this publication. Drop any
      // current-tab fallback before re-reading so an active teardown, lease
      // revocation, or external envelope update cannot reveal stale memory
      // again when the global fence returns to idle.
      memoryRecords.delete(storageKey);
      unpersistedStorageKeys.delete(storageKey);
      snapshotCache.clear();

      const storage = getBrowserStorage();
      const fenceState = storage
        ? readPreviewWriteFenceState(storage)
        : null;
      let sharedBarrierPresent = true;
      try {
        sharedBarrierPresent = Boolean(
          storage?.getItem(CUSTOMER_OWNED_SESSION_STORAGE_BARRIER_KEY),
        );
      } catch {
        sharedBarrierPresent = true;
      }

      if (
        !storage ||
        !fenceState ||
        fenceState.status === "active" ||
        sharedBarrierPresent
      ) {
        pendingClearStorageKeys.add(storageKey);
      } else {
        pendingClearStorageKeys.delete(storageKey);
      }

      onStoreChange();
    }
  };

  window.addEventListener(
    CUSTOMER_FAVOURITES_PREVIEW_CHANGE_EVENT,
    handleSameTabChange,
  );
  window.addEventListener("storage", handleStorageChange);

  return () => {
    window.removeEventListener(
      CUSTOMER_FAVOURITES_PREVIEW_CHANGE_EVENT,
      handleSameTabChange,
    );
    window.removeEventListener("storage", handleStorageChange);
  };
}

function rejectedMutation(
  operation: CustomerFavouritePreviewOperation,
  reason: Extract<
    CustomerFavouritePreviewMutationResult,
    { status: "preview-rejected" }
  >["reason"],
): CustomerFavouritePreviewMutationResult {
  return Object.freeze({
    status: "preview-rejected",
    operation,
    reason,
    evidence: "frontend-fixture-preview",
    backendSynchronization: "not-claimed",
  });
}

function settledMutation(
  operation: CustomerFavouritePreviewOperation,
  status: "preview-applied" | "preview-unchanged",
  effect: "added" | "removed" | null,
  persistence: CustomerFavouritesLocalPersistence,
  locationIds: readonly string[],
): CustomerFavouritePreviewMutationResult {
  return Object.freeze({
    status,
    operation,
    effect,
    evidence: "frontend-fixture-preview",
    backendSynchronization: "not-claimed",
    persistence,
    locationIds,
  });
}

function settleInspectableInitializationWithoutWrite(
  context: ResolvedCustomerFavouritesStoreContext,
  seedRecord: StoredCustomerFavouritesPreviewV2,
  capturedWriteFence: CustomerFavouritesPreviewWriteFenceCapture,
  initializationGeneration: number,
): CustomerFavouritePreviewMutationResult {
  if (
    initializationGeneration !== previewTeardownGeneration ||
    pendingClearStorageKeys.has(context.storageKey) ||
    isCustomerOwnedSessionStorageQuarantined()
  ) {
    return rejectedMutation("initialize", "runtime-unavailable");
  }

  if (capturedWriteFence.status === "teardown-active") {
    return rejectedMutation("initialize", "runtime-unavailable");
  }

  if (capturedWriteFence.status === "ready") {
    const currentStorage = getBrowserStorage();
    const currentFenceState = currentStorage
      ? readPreviewWriteFenceState(currentStorage)
      : null;

    if (
      currentStorage !== capturedWriteFence.storage ||
      currentFenceState?.status !== "idle" ||
      currentFenceState.token !== capturedWriteFence.globalToken
    ) {
      return rejectedMutation("initialize", "runtime-unavailable");
    }
  }

  const effective = readEffectiveRecord(context);

  if (effective.exists) {
    return settledMutation(
      "initialize",
      "preview-unchanged",
      null,
      effective.persistence,
      effective.record.locationIds,
    );
  }

  const persistence = publishMemoryFallbackRecord(context, seedRecord);
  return settledMutation(
    "initialize",
    "preview-applied",
    null,
    persistence,
    seedRecord.locationIds,
  );
}

export async function initializeCustomerFavouritesPreview(
  context: CustomerFavouritesStoreContext,
  initialLocationIds: readonly string[],
  signal?: AbortSignal,
): Promise<CustomerFavouritePreviewMutationResult> {
  const resolution = resolveContext(context);

  if (!resolution.valid) {
    return rejectedMutation("initialize", resolution.reason);
  }

  if (typeof window === "undefined") {
    return rejectedMutation("initialize", "runtime-unavailable");
  }

  if (isCustomerOwnedSessionStorageQuarantined()) {
    return rejectedMutation("initialize", "runtime-unavailable");
  }

  if (
    !Array.isArray(initialLocationIds) ||
    initialLocationIds.length > MAX_LOCATION_IDS ||
    !initialLocationIds.every(isIdentifier)
  ) {
    return rejectedMutation("initialize", "invalid-location-id");
  }

  const normalizedInitialLocationIds = normalizeLocationIds(
    initialLocationIds,
    resolution.context,
  );
  const initializationGeneration = previewTeardownGeneration;
  const lockManager = getCrossContextLockManager();
  const capturedWriteFence = capturePreviewWriteFence();

  if (signal?.aborted) {
    return rejectedMutation("initialize", "atomic-mutation-unavailable");
  }

  if (capturedWriteFence.status === "teardown-active") {
    return rejectedMutation("initialize", "runtime-unavailable");
  }

  if (!lockManager || capturedWriteFence.status !== "ready") {
    return settleInspectableInitializationWithoutWrite(
      resolution.context,
      createStoredRecord(
        resolution.context,
        normalizedInitialLocationIds,
        "memory",
      ),
      capturedWriteFence,
      initializationGeneration,
    );
  }

  try {
    return await lockManager.request(
      buildPreviewEnvelopeWebLockName(
        resolution.context.owner.customerAccountId,
      ),
      { mode: "exclusive", signal },
      (lock) => {
        if (
          !lock ||
          signal?.aborted ||
          isCustomerOwnedSessionStorageQuarantined() ||
          getBrowserStorage() !== capturedWriteFence.storage
        ) {
          return rejectedMutation(
            "initialize",
            "atomic-mutation-unavailable",
          );
        }

        const latestResolution = resolveContext(context);
        if (!latestResolution.valid) {
          return rejectedMutation("initialize", latestResolution.reason);
        }

        if (
          latestResolution.context.ownerKey !== resolution.context.ownerKey ||
          latestResolution.context.catalogFingerprint !==
            resolution.context.catalogFingerprint
        ) {
          return rejectedMutation(
            "initialize",
            "atomic-mutation-unavailable",
          );
        }

        const acquiredWriteFence = acquirePreviewWriteFence(
          capturedWriteFence.storage,
          latestResolution.context.owner.customerAccountId,
          capturedWriteFence.globalToken,
        );

        if (acquiredWriteFence.status === "storage-unavailable") {
          return settleInspectableInitializationWithoutWrite(
            latestResolution.context,
            createStoredRecord(
              latestResolution.context,
              normalizedInitialLocationIds,
              "memory",
            ),
            capturedWriteFence,
            initializationGeneration,
          );
        }

        if (acquiredWriteFence.status !== "ready") {
          return rejectedMutation(
            "initialize",
            "atomic-mutation-unavailable",
          );
        }

        let retainCreatedLease = false;

        try {
          const seedRecord = createStoredRecord(
            latestResolution.context,
            normalizedInitialLocationIds,
            acquiredWriteFence.fence.customerToken,
          );

        const atomicRead = readAtomicInitializationRecord(
          latestResolution.context,
          acquiredWriteFence.fence,
        );

        if (atomicRead.status === "pending-clear") {
          return rejectedMutation("initialize", "runtime-unavailable");
        }

        if (atomicRead.status === "storage-unavailable") {
          return settleInspectableInitializationWithoutWrite(
            latestResolution.context,
            seedRecord,
            capturedWriteFence,
            initializationGeneration,
          );
        }

        const record = atomicRead.exists ? atomicRead.record : seedRecord;
        const status = atomicRead.exists
          ? "preview-unchanged"
          : "preview-applied";

        if (atomicRead.previousRaw === JSON.stringify(record)) {
          if (
            !previewWriteFenceIsCurrent(
              atomicRead.storage,
              acquiredWriteFence.fence,
            )
          ) {
            return rejectedMutation(
              "initialize",
              "atomic-mutation-unavailable",
            );
          }

          retainCreatedLease = true;

          return settledMutation(
            "initialize",
            status,
            null,
            "browser-local",
            record.locationIds,
          );
        }

        if (
          persistAtomicBrowserRecord(
            latestResolution.context,
            atomicRead.storage,
            atomicRead.previousRaw,
            record,
            acquiredWriteFence.fence,
          )
        ) {
          retainCreatedLease = true;
          return settledMutation(
            "initialize",
            status,
            null,
            "browser-local",
            record.locationIds,
          );
        }

        if (
          pendingClearStorageKeys.has(latestResolution.context.storageKey) ||
          !previewWriteFenceIsCurrent(
            capturedWriteFence.storage,
            acquiredWriteFence.fence,
          )
        ) {
          return rejectedMutation("initialize", "runtime-unavailable");
        }

        return settledMutation(
          "initialize",
          status,
          null,
          publishMemoryFallbackRecord(
            latestResolution.context,
            record,
          ),
          record.locationIds,
        );
        } finally {
          if (
            acquiredWriteFence.createdLease &&
            !retainCreatedLease
          ) {
            releaseUnusedPreviewWriteLease(
              capturedWriteFence.storage,
              acquiredWriteFence.fence,
            );
          }
        }
      },
    );
  } catch {
    if (signal?.aborted) {
      return rejectedMutation("initialize", "atomic-mutation-unavailable");
    }

    return settleInspectableInitializationWithoutWrite(
      resolution.context,
      createStoredRecord(
        resolution.context,
        normalizedInitialLocationIds,
        "memory",
      ),
      capturedWriteFence,
      initializationGeneration,
    );
  }
}

function readAtomicInitializationRecord(
  context: ResolvedCustomerFavouritesStoreContext,
  expectedFence: CustomerFavouritesPreviewWriteFence,
):
  | {
      status: "ready";
      storage: Storage;
      previousRaw: string | null;
      exists: boolean;
      record: StoredCustomerFavouritesPreviewV2;
    }
  | { status: "pending-clear" }
  | { status: "storage-unavailable" } {
  if (pendingClearStorageKeys.has(context.storageKey)) {
    return { status: "pending-clear" };
  }

  const storage = getBrowserStorage();

  if (!storage) {
    return { status: "storage-unavailable" };
  }

  try {
    if (!previewWriteFenceIsCurrent(storage, expectedFence)) {
      return { status: "pending-clear" };
    }

    const previousRaw = storage.getItem(context.storageKey);
    const memoryRecord = memoryRecords.get(context.storageKey);
    const matchingMemoryRecord =
      unpersistedStorageKeys.has(context.storageKey) &&
      memoryRecord &&
      recordMatchesContext(memoryRecord.record, context)
        ? memoryRecord
        : null;

    if (previousRaw === null) {
      return {
        status: "ready",
        storage,
        previousRaw,
        exists: Boolean(matchingMemoryRecord?.exists),
        record: createStoredRecord(
          context,
          matchingMemoryRecord?.record.locationIds ?? EMPTY_LOCATION_IDS,
          expectedFence.customerToken,
        ),
      };
    }

    const parsed = JSON.parse(previousRaw) as unknown;
    const parsedRecord = parseStoredRecord(
      parsed,
      context,
      expectedFence.customerToken,
    );

    if (!parsedRecord) {
      return {
        status: "ready",
        storage,
        previousRaw,
        exists: Boolean(matchingMemoryRecord?.exists),
        record: createStoredRecord(
          context,
          matchingMemoryRecord?.record.locationIds ?? EMPTY_LOCATION_IDS,
          expectedFence.customerToken,
        ),
      };
    }

    return {
      status: "ready",
      storage,
      previousRaw,
      exists: true,
      record: createStoredRecord(
        context,
        normalizeLocationIds(parsedRecord.locationIds, context),
        expectedFence.customerToken,
      ),
    };
  } catch {
    return { status: "storage-unavailable" };
  }
}

function readAtomicBrowserRecord(
  context: ResolvedCustomerFavouritesStoreContext,
  expectedFence: CustomerFavouritesPreviewWriteFence,
):
  | {
      status: "ready";
      storage: Storage;
      previousRaw: string | null;
      record: StoredCustomerFavouritesPreviewV2;
    }
  | { status: "unavailable" } {
  if (pendingClearStorageKeys.has(context.storageKey)) {
    return { status: "unavailable" };
  }

  const storage = getBrowserStorage();

  if (!storage) {
    return { status: "unavailable" };
  }

  try {
    if (!previewWriteFenceIsCurrent(storage, expectedFence)) {
      return { status: "unavailable" };
    }

    const previousRaw = storage.getItem(context.storageKey);

    if (previousRaw === null) {
      const memoryRecord = memoryRecords.get(context.storageKey);
      const seedLocationIds =
        memoryRecord && recordMatchesContext(memoryRecord.record, context)
          ? memoryRecord.record.locationIds
          : EMPTY_LOCATION_IDS;

      return {
        status: "ready",
        storage,
        previousRaw,
        record: createStoredRecord(
          context,
          seedLocationIds,
          expectedFence.customerToken,
        ),
      };
    }

    const parsed = JSON.parse(previousRaw) as unknown;
    const parsedRecord = parseStoredRecord(
      parsed,
      context,
      expectedFence.customerToken,
    );

    if (!parsedRecord) {
      return { status: "unavailable" };
    }

    return {
      status: "ready",
      storage,
      previousRaw,
      record: createStoredRecord(
        context,
        normalizeLocationIds(parsedRecord.locationIds, context),
        expectedFence.customerToken,
      ),
    };
  } catch {
    return { status: "unavailable" };
  }
}

function restoreAtomicBrowserRecord(
  storage: Storage,
  storageKey: string,
  previousRaw: string | null,
) {
  try {
    if (previousRaw === null) {
      storage.removeItem(storageKey);
      return storage.getItem(storageKey) === null;
    }

    storage.setItem(storageKey, previousRaw);
    return storage.getItem(storageKey) === previousRaw;
  } catch {
    return false;
  }
}

function discardStaleAtomicBrowserRecord(
  storage: Storage,
  storageKey: string,
  serializedRecord: string,
) {
  try {
    if (storage.getItem(storageKey) === serializedRecord) {
      storage.removeItem(storageKey);
    }

    return storage.getItem(storageKey) !== serializedRecord;
  } catch {
    return false;
  }
}

function persistAtomicBrowserRecord(
  context: ResolvedCustomerFavouritesStoreContext,
  storage: Storage,
  previousRaw: string | null,
  record: StoredCustomerFavouritesPreviewV2,
  expectedFence: CustomerFavouritesPreviewWriteFence,
) {
  const serialized = JSON.stringify(record);

  if (
    !previewWriteFenceIsCurrent(
      storage,
      expectedFence,
    )
  ) {
    return false;
  }

  try {
    storage.setItem(context.storageKey, serialized);

    const writtenValue = storage.getItem(context.storageKey);
    const fenceRemainsCurrent = previewWriteFenceIsCurrent(
      storage,
      expectedFence,
    );

    if (!fenceRemainsCurrent) {
      discardStaleAtomicBrowserRecord(
        storage,
        context.storageKey,
        serialized,
      );
      memoryRecords.delete(context.storageKey);
      unpersistedStorageKeys.delete(context.storageKey);
      dispatchPreviewChange(context.storageKey);
      return false;
    }

    if (writtenValue !== serialized) {
      restoreAtomicBrowserRecord(storage, context.storageKey, previousRaw);
      return false;
    }
  } catch {
    if (
      previewWriteFenceIsCurrent(
        storage,
        expectedFence,
      )
    ) {
      restoreAtomicBrowserRecord(storage, context.storageKey, previousRaw);
    } else {
      discardStaleAtomicBrowserRecord(
        storage,
        context.storageKey,
        serialized,
      );
      memoryRecords.delete(context.storageKey);
      unpersistedStorageKeys.delete(context.storageKey);
      dispatchPreviewChange(context.storageKey);
    }
    return false;
  }

  memoryRecords.set(context.storageKey, {
    exists: true,
    persistence: "browser-local",
    record,
  });
  unpersistedStorageKeys.delete(context.storageKey);

  if (
    !previewWriteFenceIsCurrent(
      storage,
      expectedFence,
    )
  ) {
    discardStaleAtomicBrowserRecord(
      storage,
      context.storageKey,
      serialized,
    );
    memoryRecords.delete(context.storageKey);
    unpersistedStorageKeys.delete(context.storageKey);
    dispatchPreviewChange(context.storageKey);
    return false;
  }

  pendingClearStorageKeys.delete(context.storageKey);
  dispatchPreviewChange(context.storageKey);
  return true;
}

async function mutateCustomerFavouritesPreview(
  context: CustomerFavouritesStoreContext,
  locationId: string,
  operation: "add" | "remove" | "toggle",
  signal?: AbortSignal,
): Promise<CustomerFavouritePreviewMutationResult> {
  const resolution = resolveContext(context);

  if (!resolution.valid) {
    return rejectedMutation(operation, resolution.reason);
  }

  if (typeof window === "undefined") {
    return rejectedMutation(operation, "runtime-unavailable");
  }

  if (isCustomerOwnedSessionStorageQuarantined()) {
    return rejectedMutation(operation, "runtime-unavailable");
  }

  if (!isIdentifier(locationId)) {
    return rejectedMutation(operation, "invalid-location-id");
  }

  if (!resolution.context.publicLocationIds.has(locationId)) {
    return rejectedMutation(operation, "location-unavailable");
  }

  const lockManager = getCrossContextLockManager();

  if (!lockManager || signal?.aborted) {
    return rejectedMutation(operation, "atomic-mutation-unavailable");
  }

  const capturedWriteFence = capturePreviewWriteFence();

  if (capturedWriteFence.status !== "ready") {
    return rejectedMutation(operation, "atomic-mutation-unavailable");
  }

  try {
    return await lockManager.request(
      buildPreviewEnvelopeWebLockName(
        resolution.context.owner.customerAccountId,
      ),
      { mode: "exclusive", signal },
      (lock) => {
        if (
          !lock ||
          signal?.aborted ||
          isCustomerOwnedSessionStorageQuarantined() ||
          getBrowserStorage() !== capturedWriteFence.storage
        ) {
          return rejectedMutation(operation, "atomic-mutation-unavailable");
        }

        const latestResolution = resolveContext(context);
        if (!latestResolution.valid) {
          return rejectedMutation(operation, latestResolution.reason);
        }

        if (
          latestResolution.context.ownerKey !== resolution.context.ownerKey ||
          latestResolution.context.catalogFingerprint !==
            resolution.context.catalogFingerprint ||
          !latestResolution.context.publicLocationIds.has(locationId)
        ) {
          return rejectedMutation(operation, "atomic-mutation-unavailable");
        }

        const acquiredWriteFence = acquirePreviewWriteFence(
          capturedWriteFence.storage,
          latestResolution.context.owner.customerAccountId,
          capturedWriteFence.globalToken,
        );

        if (acquiredWriteFence.status !== "ready") {
          return rejectedMutation(operation, "atomic-mutation-unavailable");
        }

        let retainCreatedLease = false;

        try {

        const atomicRead = readAtomicBrowserRecord(
          latestResolution.context,
          acquiredWriteFence.fence,
        );
        if (atomicRead.status !== "ready") {
          return rejectedMutation(operation, "atomic-mutation-unavailable");
        }

        const currentLocationIds = atomicRead.record.locationIds;
        const currentlySaved = currentLocationIds.includes(locationId);
        const shouldSave =
          operation === "add" ||
          (operation === "toggle" && !currentlySaved);
        const shouldRemove =
          operation === "remove" ||
          (operation === "toggle" && currentlySaved);
        const nextLocationIds = shouldSave
          ? currentlySaved
            ? currentLocationIds
            : [...currentLocationIds, locationId]
          : shouldRemove && currentlySaved
            ? currentLocationIds.filter(
                (candidate) => candidate !== locationId,
              )
            : currentLocationIds;

        if (nextLocationIds === currentLocationIds) {
          if (
            atomicRead.previousRaw !== JSON.stringify(atomicRead.record)
          ) {
            if (
              !persistAtomicBrowserRecord(
                latestResolution.context,
                atomicRead.storage,
                atomicRead.previousRaw,
                atomicRead.record,
                acquiredWriteFence.fence,
              )
            ) {
              return rejectedMutation(
                operation,
                "atomic-mutation-unavailable",
              );
            }

            retainCreatedLease = true;
          } else if (
            !previewWriteFenceIsCurrent(
              atomicRead.storage,
              acquiredWriteFence.fence,
            )
          ) {
            return rejectedMutation(
              operation,
              "atomic-mutation-unavailable",
            );
          }

          retainCreatedLease = true;

          return settledMutation(
            operation,
            "preview-unchanged",
            null,
            "browser-local",
            currentLocationIds,
          );
        }

        const record = createStoredRecord(
          latestResolution.context,
          nextLocationIds,
          acquiredWriteFence.fence.customerToken,
        );

        if (
          !persistAtomicBrowserRecord(
            latestResolution.context,
            atomicRead.storage,
            atomicRead.previousRaw,
            record,
            acquiredWriteFence.fence,
          )
        ) {
          return rejectedMutation(operation, "atomic-mutation-unavailable");
        }

        retainCreatedLease = true;

        return settledMutation(
          operation,
          "preview-applied",
          shouldSave ? "added" : "removed",
          "browser-local",
          record.locationIds,
        );
        } finally {
          if (
            acquiredWriteFence.createdLease &&
            !retainCreatedLease
          ) {
            releaseUnusedPreviewWriteLease(
              capturedWriteFence.storage,
              acquiredWriteFence.fence,
            );
          }
        }
      },
    );
  } catch {
    return rejectedMutation(operation, "atomic-mutation-unavailable");
  }
}

export function addCustomerFavouriteLocationPreview(
  context: CustomerFavouritesStoreContext,
  locationId: string,
  signal?: AbortSignal,
) {
  return mutateCustomerFavouritesPreview(context, locationId, "add", signal);
}

export function removeCustomerFavouriteLocationPreview(
  context: CustomerFavouritesStoreContext,
  locationId: string,
  signal?: AbortSignal,
) {
  return mutateCustomerFavouritesPreview(
    context,
    locationId,
    "remove",
    signal,
  );
}

export function toggleCustomerFavouriteLocationPreview(
  context: CustomerFavouritesStoreContext,
  locationId: string,
  signal?: AbortSignal,
) {
  return mutateCustomerFavouritesPreview(
    context,
    locationId,
    "toggle",
    signal,
  );
}

export function clearCustomerFavouritesPreview(
  customerAccountId: string,
) {
  const storageKey = buildCustomerFavouritesPreviewStorageKey(
    customerAccountId,
  );

  if (!storageKey || typeof window === "undefined") {
    return false;
  }

  previewTeardownGeneration += 1;

  const legacyStorageKey =
    buildLegacyCustomerFavouritesPreviewStorageKey(customerAccountId);
  const writeLeaseKey = buildPreviewWriteLeaseKey(customerAccountId);
  const ownedStorageKeys = [
    storageKey,
    ...(legacyStorageKey ? [legacyStorageKey] : []),
    writeLeaseKey,
  ];

  const storage = getBrowserStorage();
  const teardownFence = storage ? beginPreviewTeardown(storage) : null;

  memoryRecords.delete(storageKey);
  unpersistedStorageKeys.delete(storageKey);
  snapshotCache.clear();

  let removalVerified = Boolean(storage && teardownFence);

  try {
    if (!storage || !teardownFence) {
      throw new Error("Browser local storage is unavailable.");
    }

    for (const ownedStorageKey of ownedStorageKeys) {
      if (!previewTeardownIsCurrent(storage, teardownFence)) {
        removalVerified = false;
        break;
      }

      storage.removeItem(ownedStorageKey);
      if (storage.getItem(ownedStorageKey) !== null) {
        removalVerified = false;
      }
    }
  } catch {
    removalVerified = false;
  }

  const cleared = Boolean(
    storage &&
      teardownFence &&
      removalVerified &&
      previewTeardownIsCurrent(storage, teardownFence) &&
      completePreviewTeardown(storage, teardownFence),
  );

  if (teardownFence) {
    finishPreviewTeardownAttempt(teardownFence, cleared);
  }

  if (cleared) {
    pendingClearStorageKeys.delete(storageKey);
  } else {
    pendingClearStorageKeys.add(storageKey);
  }

  dispatchPreviewChange(storageKey);
  return cleared;
}

function collectPersistedCustomerFavouritesPreviewKeys(storage: Storage) {
  const storageKeys: string[] = [];

  try {
    for (let index = 0; index < storage.length; index += 1) {
      const storageKey = storage.key(index);

      if (
        storageKey &&
        (storageKey.startsWith(
          CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KEY_PREFIX,
        ) ||
          storageKey.startsWith(
            LEGACY_CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KEY_PREFIX,
          ) ||
          storageKey.startsWith(
            CUSTOMER_FAVOURITES_PREVIEW_WRITE_LEASE_KEY_PREFIX,
          ))
      ) {
        storageKeys.push(storageKey);
      }
    }

    return storageKeys;
  } catch {
    return null;
  }
}

function getCustomerAccountIdFromPreviewStorageKey(storageKey: string) {
  const prefix = [
    CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KEY_PREFIX,
    LEGACY_CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KEY_PREFIX,
    CUSTOMER_FAVOURITES_PREVIEW_WRITE_LEASE_KEY_PREFIX,
  ].find((candidate) => storageKey.startsWith(candidate));

  if (!prefix) {
    return null;
  }

  try {
    const customerAccountId = decodeURIComponent(
      storageKey.slice(prefix.length),
    );

    const expectedStorageKey =
      prefix === CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KEY_PREFIX
        ? buildCustomerFavouritesPreviewStorageKey(customerAccountId)
        : prefix === LEGACY_CUSTOMER_FAVOURITES_PREVIEW_STORAGE_KEY_PREFIX
          ? buildLegacyCustomerFavouritesPreviewStorageKey(customerAccountId)
          : isIdentifier(customerAccountId)
            ? buildPreviewWriteLeaseKey(customerAccountId)
            : null;

    return expectedStorageKey === storageKey
      ? customerAccountId
      : null;
  } catch {
    return null;
  }
}

export interface ClearAllCustomerFavouritesPreviewsResult {
  cleared: boolean;
  customerAccountIds: readonly string[];
}

export interface CustomerFavouritesPreviewCustomerDiscovery {
  verified: boolean;
  customerAccountIds: readonly string[];
}

/**
 * First phase of an unknown-auth teardown. Discovery is read-only so auth can
 * clear every customer's absorbing action lock before deleting the preview
 * keys that preserve those exact customer identities for a later retry.
 */
export function discoverCustomerFavouritesPreviewCustomers(): CustomerFavouritesPreviewCustomerDiscovery {
  if (typeof window === "undefined") {
    return Object.freeze({
      verified: false,
      customerAccountIds: EMPTY_LOCATION_IDS,
    });
  }

  const storage = getBrowserStorage();
  const storageKeys = new Set<string>([
    ...memoryRecords.keys(),
    ...unpersistedStorageKeys,
    ...pendingClearStorageKeys,
  ]);
  let verified = false;

  if (storage) {
    const persistedStorageKeys =
      collectPersistedCustomerFavouritesPreviewKeys(storage);

    if (persistedStorageKeys) {
      verified = true;
      for (const storageKey of persistedStorageKeys) {
        storageKeys.add(storageKey);
      }
    }
  }

  const customerAccountIds = new Set<string>();

  for (const storageKey of storageKeys) {
    const customerAccountId =
      getCustomerAccountIdFromPreviewStorageKey(storageKey);

    if (customerAccountId) {
      customerAccountIds.add(customerAccountId);
    }
  }

  return Object.freeze({
    verified,
    customerAccountIds: Object.freeze([...customerAccountIds]),
  });
}

/**
 * Unknown or unreadable auth state cannot identify one safe customer key.
 * The central auth boundary uses this exhaustive prefix sweep and releases
 * its quarantine only when a second enumeration proves every preview gone.
 */
export function clearAllCustomerFavouritesPreviews() {
  if (typeof window === "undefined") {
    return Object.freeze({
      cleared: false,
      customerAccountIds: EMPTY_LOCATION_IDS,
    }) satisfies ClearAllCustomerFavouritesPreviewsResult;
  }

  previewTeardownGeneration += 1;

  const internalStorageKeys = new Set<string>([
    ...memoryRecords.keys(),
    ...unpersistedStorageKeys,
    ...pendingClearStorageKeys,
  ]);
  const customerAccountIds = new Set<string>();
  const clearInternalState = () => {
    for (const storageKey of internalStorageKeys) {
      const customerAccountId =
        getCustomerAccountIdFromPreviewStorageKey(storageKey);

      if (customerAccountId) {
        customerAccountIds.add(customerAccountId);
      }

      memoryRecords.delete(storageKey);
      unpersistedStorageKeys.delete(storageKey);
      pendingClearStorageKeys.add(storageKey);
      dispatchPreviewChange(storageKey);
    }

    snapshotCache.clear();
  };
  const failedVerificationResult = () => {
    clearInternalState();

    return Object.freeze({
      cleared: false,
      customerAccountIds: Object.freeze([...customerAccountIds]),
    }) satisfies ClearAllCustomerFavouritesPreviewsResult;
  };
  const storage = getBrowserStorage();

  if (!storage) {
    return failedVerificationResult();
  }

  const teardownFence = beginPreviewTeardown(storage);
  if (!teardownFence) {
    return failedVerificationResult();
  }

  const persistedStorageKeys =
    collectPersistedCustomerFavouritesPreviewKeys(storage);

  if (!persistedStorageKeys) {
    finishPreviewTeardownAttempt(teardownFence, false);
    return failedVerificationResult();
  }

  const storageKeys = new Set<string>([
    ...persistedStorageKeys,
    ...internalStorageKeys,
  ]);
  let cleared = true;

  for (const storageKey of storageKeys) {
    const customerAccountId =
      getCustomerAccountIdFromPreviewStorageKey(storageKey);

    if (customerAccountId) {
      customerAccountIds.add(customerAccountId);
    }

    memoryRecords.delete(storageKey);
    unpersistedStorageKeys.delete(storageKey);

    try {
      if (!previewTeardownIsCurrent(storage, teardownFence)) {
        throw new Error("Customer Favourites teardown ownership was lost.");
      }

      storage.removeItem(storageKey);

      if (storage.getItem(storageKey) !== null) {
        throw new Error("Customer Favourites preview removal was not verified.");
      }

      pendingClearStorageKeys.delete(storageKey);
    } catch {
      pendingClearStorageKeys.add(storageKey);
      cleared = false;
    }

    dispatchPreviewChange(storageKey);
  }

  const remainingStorageKeys = previewTeardownIsCurrent(
    storage,
    teardownFence,
  )
    ? collectPersistedCustomerFavouritesPreviewKeys(storage)
    : null;

  if (!remainingStorageKeys || remainingStorageKeys.length > 0) {
    cleared = false;
  }

  if (
    cleared &&
    previewTeardownIsCurrent(storage, teardownFence) &&
    !completePreviewTeardown(storage, teardownFence)
  ) {
    cleared = false;
  }

  finishPreviewTeardownAttempt(teardownFence, cleared);

  if (!cleared) {
    for (const storageKey of storageKeys) {
      pendingClearStorageKeys.add(storageKey);
    }
  }

  if (cleared) {
    pendingClearStorageKeys.clear();
  }

  snapshotCache.clear();

  return Object.freeze({
    cleared,
    customerAccountIds: Object.freeze([...customerAccountIds]),
  }) satisfies ClearAllCustomerFavouritesPreviewsResult;
}

export function useCustomerFavouritesPreview(
  context: CustomerFavouritesStoreContext,
) {
  const customerAccountId = context.owner.customerAccountId;
  const subscribe = useCallback(
    (onStoreChange: () => void) =>
      subscribeToCustomerFavouritesPreview(
        customerAccountId,
        onStoreChange,
      ),
    [customerAccountId],
  );
  const getSnapshot = useCallback(
    () => readCustomerFavouritesPreview(context),
    [context],
  );

  return useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => RUNTIME_UNAVAILABLE_STATE,
  );
}
